import { designerControl } from "$utils/designer-control";
import { generateUUID } from "$utils/uuid";
import { multiSelection } from "../store/multiSelection";

let copiedStep = null;
let copiedSteps = [];

const actionMap = {
  c: { fn: copyBlock, ctrlOrMeta: true },
  v: { fn: pasteBlock, ctrlOrMeta: true },
  d: { fn: duplicateBlock, ctrlOrMeta: true },
  x: { fn: cutBlock, ctrlOrMeta: true },
  "/": { fn: toggleStepSkip, ctrlOrMeta: true },
  Delete: { fn: deleteBlock, ctrlOrMeta: false },
  Backspace: { fn: deleteBlock, ctrlOrMeta: false },
};

function findNeedle(parentNode, needle, update_fn = null) {
  let result = {
    target: null,
    needle: null,
    needleIndex: -1,
    needleSequence: null,
    needleBranch: -1,
    needleParent: null,
    needleParentIndex: -1,
    needleParentSequence: null,
  };

  if (!parentNode) {
    return result;
  }

  let sequence = [];
  if (Object.prototype.hasOwnProperty.call(parentNode, "sequence")) {
    sequence = parentNode.sequence;
  }

  if (!needle) {
    if (sequence.length > 0) {
      result.needle = sequence[0];
      result.needleIndex = 0;
      result.needleSequence = sequence;
      result.target = result.needle;
    }
    return result;
  }

  return findNeedleInSeq(parentNode, sequence, needle, result, update_fn);
}

function findNeedleInSwitch(parentNode, switchNode, needle, result, update_fn) {
  const branches = Object.entries(switchNode.branches);
  for (let i = 0; i < branches.length; i++) {
    const [_, substeps] = branches[i];
    result = findNeedleInSeq(switchNode, substeps, needle, result, update_fn);
    if (result.needle) {
      if (result.needleBranch === -1) {
        result.needleBranch = i;
      }
      break;
    }
  }

  return result;
}

function findNeedleInSeq(parentNode, sequence, needle, result, update_fn) {
  for (let i = 0; i < sequence.length; i++) {
    const current = sequence[i];
    if (current.id === needle) {
      result.needle = current;
      result.needleIndex = i;
      result.needleSequence = sequence;
      if (update_fn) {
        const updated = update_fn(result);
        if (updated) {
          // If we did a bulk delete/manual update, we don't want to re-assign sequence[i]
          if (updated.op === "manual_update") {
            // Do nothing for sequence mutation, it was handled
          } else {
            sequence[i] = result.needle = updated.needle;
            switch (updated.op) {
              case "insert_after":
                if (updated.blocks) {
                  sequence.splice(i + 1, 0, ...updated.blocks);
                } else {
                  sequence.splice(i + 1, 0, updated.block);
                }
                break;
              case "delete":
                sequence.splice(i, 1);
                break;
              case "move_up":
                if (i > 0) {
                  sequence.splice(i, 1);
                  sequence.splice(i - 1, 0, updated.needle);
                }
                break;
              case "move_down":
                if (i < sequence.length - 1) {
                  sequence.splice(i, 1);
                  sequence.splice(i + 1, 0, updated.needle);
                }
                break;
              case "no_change":
              default:
                break;
            }
          }

          if (updated.focus) {
            result.focus = updated.focus;
          }
        }
      }
      break;
    } else {
      if (current.componentType === "switch") {
        result = findNeedleInSwitch(parentNode, current, needle, result, update_fn);
        if (result.needle) {
          if (!result.needleParent) {
            result.needleParent = current;
            result.needleParentIndex = i;
            result.needleParentSequence = sequence;
          }
          break;
        }
      }
    }
  }

  return result;
}

function navigateUp(parentNode, key, result) {
  if (result.needleIndex == 0) {
    result.target = result.needleParent;
  } else {
    result = enterStep(result.needleSequence[result.needleIndex - 1], result);
  }

  return result;
}

function navigateDown(parentNode, key, result) {
  if (result.needleIndex == result.needleSequence.length - 1) {
    result.target = result.needleParent;
  } else {
    result = enterStep(result.needleSequence[result.needleIndex + 1], result);
  }

  return result;
}

function navigateLeft(parentNode, key, result) {
  // navigate left can only happend when we are in a switch
  if (result.needleParent && result.needleParent.componentType === "switch") {
    let targetBranch = result.needleBranch - 1;
    const branches = Object.entries(result.needleParent.branches);
    for (let i = targetBranch; i >= 0; i--) {
      const [_, substeps] = branches[i];
      if (substeps.length > 0) {
        result.target = substeps[Math.min(substeps.length - 1, result.needleIndex)];
        break;
      }
    }
    if (!result.target) {
      result.target = result.needleParent;
    }
  }

  return result;
}

function navigateRight(parentNode, key, result) {
  // navigate right can only happend when we are in a switch
  if (result.needleParent && result.needleParent.componentType === "switch") {
    let targetBranch = result.needleBranch + 1;
    const branches = Object.entries(result.needleParent.branches);
    for (let i = targetBranch; i < branches.length; i++) {
      const [_, substeps] = branches[i];
      if (substeps.length > 0) {
        result.target = substeps[Math.min(substeps.length - 1, result.needleIndex)];
        break;
      }
    }
    if (!result.target) {
      result.target = result.needleParent;
    }
  }

  return result;
}

function navigateEnter(parentNode, key, result) {
  // navigate enter can only happend when we are in a switch
  return enterStep(result.needle, result);
}

function navigateToTarget(parentNode, key, result) {
  if (!result.needle) return result;
  if (result.target) return result;

  if (key === "ArrowUp") {
    result = navigateUp(parentNode, key, result);
  } else if (key === "ArrowDown") {
    result = navigateDown(parentNode, key, result);
  } else if (key === "ArrowLeft") {
    result = navigateLeft(parentNode, key, result);
  } else if (key === "ArrowRight") {
    result = navigateRight(parentNode, key, result);
  } else if (key === "Enter") {
    result = navigateEnter(parentNode, key, result);
  }
  return result;
}

function navigateDefinition(parentNode, needle, key) {
  let result = findNeedle(parentNode, needle);
  result = navigateToTarget(parentNode, key, result);
  return result;
}

function enterStep(component, result) {
  if (!component) return result;

  if (component.componentType === "switch") {
    const branches = Object.entries(component.branches);
    for (let i = 0; i < branches.length; i++) {
      const [_, substeps] = branches[i];
      if (substeps.length > 0) {
        result.target = substeps[0];
        break;
      }
    }
  }

  if (!result.target) {
    result.target = component;
  }
  return result;
}

function toggleStepSkip(result) {
  if (result.needle) {
    let metadata = result.needle.metadata || {};
    let skip = metadata.skip || false;
    metadata.skip = !skip;
    result.needle.metadata = metadata;
  }
  return {
    op: "no_change",
    needle: result.needle,
  };
}

// Helper to regenerate IDs and update references
function regenerateIds(blocks) {
  const idMap = new Map();

  // Pass 1: Collect IDs and generate new ones
  const collect = (block) => {
    idMap.set(block.id, generateUUID("w"));
    if (block.branches) {
      Object.values(block.branches).forEach((seq) => seq.forEach(collect));
    }
    if (block.sequence) {
      block.sequence.forEach(collect);
    }
  };
  blocks.forEach(collect);

  // Pass 2: Replace IDs
  const replace = (obj) => {
    if (Array.isArray(obj)) {
      obj.forEach(replace);
    } else if (typeof obj === "object" && obj !== null) {
      // If it's a block we are remapping
      if (obj.id && idMap.has(obj.id)) {
        obj.id = idMap.get(obj.id);
      }

      // Special handling for Automator block properties that reference other blocks
      // e.g. properties: { item: '$step["w.ygt4d3"].default' }
      if (obj.properties) {
        for (const [key, val] of Object.entries(obj.properties)) {
          if (typeof val === "string") {
            let newVal = val;
            let changed = false;
            for (const [oldId, newId] of idMap.entries()) {
              if (newVal.includes(oldId)) {
                newVal = newVal.replaceAll(oldId, newId);
                changed = true;
              }
            }
            if (changed) {
              obj.properties[key] = newVal;
            }
          }
        }
      }

      for (const key in obj) {
        if (key === "id") continue;
        const val = obj[key];
        if (typeof val === "string") {
          if (idMap.has(val)) {
            // Exact match
            obj[key] = idMap.get(val);
          } else {
            // Check for substrings (e.g. "$step['w.xyz'].prop")
            // We iterate over keys - optimization: only check if string contains "w."?
            // Since we only have a limited set of new IDs, iterating is fine.
            let newVal = val;
            let changed = false;
            for (const [oldId, newId] of idMap.entries()) {
              if (newVal.includes(oldId)) {
                newVal = newVal.replaceAll(oldId, newId);
                changed = true;
              }
            }
            if (changed) {
              obj[key] = newVal;
            }
          }
        } else if (typeof val === "object" && val !== null) {
          replace(val);
        }
      }
    }
  };

  blocks.forEach(replace);
  return idMap;
}

function getSelectedBlocks(needle, defs) {
  const selectedIds = multiSelection.getAll();
  // If multi-selection is active and contains the current needle (or we want to prioritize it)
  // we fetch all those blocks. For now, let's just get the blocks corresponding to IDs.

  if (selectedIds.length > 1) {
    const blocks = [];
    // We need to find all blocks. This is inefficient if we scan the whole tree for each ID,
    // but typically selection size is small.
    // Optimization: Single pass to find all IDs.

    // Simple recursive finder
    // We traverse the definition in order.
    // If a block is selected, we add it and DO NOT recurse into its children.
    // This ensures that:
    // 1. We preserve the relative order of blocks (definition order).
    // 2. We don't include descendants if their ancestor is already selected (preventing duplication).
    const findBlocks = (sequence) => {
      for (const block of sequence) {
        if (selectedIds.includes(block.id)) {
          blocks.push(block);
          // Optimization & Logic: If a parent is selected, we treat it as a single unit.
          // We don't need to find selected children inside it because they will be
          // handled as part of the parent block actions (copy/move/delete).
        } else {
          if (block.branches) {
            Object.values(block.branches).forEach(findBlocks);
          }
          if (block.sequence) {
            findBlocks(block.sequence);
          }
        }
      }
    };

    findBlocks(defs.sequence || []);

    return blocks;
  }

  return needle ? [needle] : [];
}

function copyBlock(result, defs) {
  const blocks = getSelectedBlocks(result.needle, defs);

  if (blocks.length > 0) {
    copyBlocks(blocks);
  }

  return {
    op: "no_change",
    needle: result.needle,
  };
}

function copyBlocks(blocks) {
  if (blocks && blocks.length > 0) {
    copiedSteps = structuredClone(blocks);
    copiedStep = copiedSteps[0]; // For backward compatibility
  }
}

function pasteBlock(result) {
  // Prefer copiedSteps if available and consistent
  let sourceBlocks = copiedSteps;
  if (sourceBlocks.length === 0 && copiedStep) {
    sourceBlocks = [copiedStep];
  }

  if (sourceBlocks.length > 0) {
    const newBlocks = structuredClone(sourceBlocks);
    regenerateIds(newBlocks);

    // Add "Copy of" prefix to top-level blocks
    newBlocks.forEach((b) => {
      b.name = `Copy of ${b.name}`;
    });

    return {
      op: "insert_after",
      needle: result.needle,
      blocks: newBlocks,
      focus: newBlocks[newBlocks.length - 1].id,
    };
  }

  return {
    op: "no_change",
    needle: result.needle,
  };
}

function pasteBlocks(result) {
  return pasteBlock(result);
}

function duplicateBlock(result, defs) {
  const blocks = getSelectedBlocks(result.needle, defs);

  if (blocks.length > 1) {
    return duplicateBlocks(blocks, result);
  }

  if (result.needle) {
    const newBlocks = [structuredClone(result.needle)];
    regenerateIds(newBlocks);

    newBlocks[0].name = `Copy of ${newBlocks[0].name}`;

    return {
      op: "insert_after",
      needle: result.needle,
      blocks: newBlocks,
      focus: newBlocks[0].id,
    };
  }

  return {
    op: "no_change",
    needle: result.needle,
  };
}

function duplicateBlocks(blocks, result) {
  if (blocks && blocks.length > 0) {
    const newBlocks = structuredClone(blocks);
    regenerateIds(newBlocks);

    newBlocks.forEach((b) => {
      b.name = `Copy of ${b.name}`;
    });

    return {
      op: "insert_after",
      needle: result.needle || blocks[blocks.length - 1], // Insert after target or last of source (if result is vague)
      blocks: newBlocks,
      focus: newBlocks[newBlocks.length - 1].id,
    };
  }
  return { op: "no_change", needle: result.needle };
}

function cutBlock(result, defs) {
  const blocks = getSelectedBlocks(result.needle, defs);
  if (blocks.length > 0) {
    copyBlocks(blocks);
  }
  return deleteBlock(result, defs);
}

function bulkDelete(parent, ids) {
  // recursive traversal
  if (parent.sequence) {
    // iterate backwards to safely splice
    for (let i = parent.sequence.length - 1; i >= 0; i--) {
      if (ids.includes(parent.sequence[i].id)) {
        parent.sequence.splice(i, 1);
      } else {
        bulkDelete(parent.sequence[i], ids);
      }
    }
  }
  if (parent.branches) {
    Object.values(parent.branches).forEach((seq) => {
      for (let i = seq.length - 1; i >= 0; i--) {
        if (ids.includes(seq[i].id)) {
          seq.splice(i, 1);
        } else {
          bulkDelete(seq[i], ids);
        }
      }
    });
  }
}

function deleteBlock(result, defs) {
  const selectedIds = multiSelection.getAll();

  if (selectedIds.length > 1) {
    // Bulk delete mode
    bulkDelete(defs, selectedIds);
    multiSelection.clear(); // Clear selection after deletion

    // Focus parent or something reasonable.
    // result.needleParent is the parent of the currently focused item (which was likely deleted)
    return {
      op: "manual_update",
      focus: result.needleParent ? result.needleParent.id : undefined,
    };
  }

  let focus = undefined;
  if (result.needleSequence && result.needleSequence.length > 1) {
    if (result.needleIndex === result.needleSequence.length - 1) {
      focus = result.needleSequence[result.needleSequence.length - 2].id;
    } else {
      focus = result.needleSequence[result.needleIndex + 1].id;
    }
  }

  return {
    op: "delete",
    needle: result.needle,
    focus,
  };
}

function moveBlock(result, direction) {
  if (direction === "ArrowUp") {
    return {
      op: "move_up",
      needle: result.needle,
    };
  } else {
    return {
      op: "move_down",
      needle: result.needle,
    };
  }
}

function hasTextSelection() {
  const selection = window.getSelection();
  return !!selection && selection.toString().length > 0;
}

function handleKeydown(event, designer, isReadonly) {
  // Let the browser copy selected text instead of hijacking Cmd/Ctrl+C for block copy.
  if (event.key === "c" && event.metaKey ^ event.ctrlKey && hasTextSelection()) {
    return;
  }

  let preventDefault = true;
  switch (event.key) {
    case "z":
      if (isReadonly) return;
      if (event.metaKey ^ event.ctrlKey) {
        if (event.shiftKey) {
          designerControl.redo();
        } else {
          designerControl.undo();
        }
      }
      break;
    case "+":
      designer.api.viewport.zoom(true);
      break;
    case "-":
      designer.api.viewport.zoom(false);
      break;
    case "f":
      designerControl.resetView();
      break;
    case "Enter":
    case "ArrowDown":
    case "ArrowUp":
    case "ArrowLeft":
    case "ArrowRight": {
      const defs = designer.getDefinition();
      const stepId = designer.getSelectedStepId();
      let result;
      if (event.metaKey ^ event.ctrlKey && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
        result = findNeedle(defs, stepId, (result) => moveBlock(result, event.key));
        designer.replaceDefinition(JSON.parse(JSON.stringify(defs)));
        if (result.focus) {
          designer.selectStepById(result.focus);
          designer.moveViewportToStep(result.focus);
        }
      } else {
        result = navigateDefinition(defs, stepId, event.key);
      }

      if (result.target) {
        designer.selectStepById(result.target.id);
        designer.moveViewportToStep(result.target.id);
      }
      break;
    }
    default: {
      if (isReadonly) return;
      if (
        Object.prototype.hasOwnProperty.call(actionMap, event.key) &&
        ((actionMap[event.key].ctrlOrMeta && event.metaKey ^ event.ctrlKey) ||
          !actionMap[event.key].ctrlOrMeta)
      ) {
        const defs = designer.getDefinition();
        const stepId = designer.getSelectedStepId();

        // Pass defs to findNeedle callback or wrapper so copy/duplicate can access it
        // We modify the action function call signature or wrap it.
        const actionFn = actionMap[event.key].fn;

        const result = findNeedle(defs, stepId, (res) => actionFn(res, defs));

        if (event.key !== "c") designer.replaceDefinition(JSON.parse(JSON.stringify(defs)));
        if (result.focus) {
          // need to wait a bit for deletions otherwise we might end up deleting two steps
          const timeout = event.key === "Delete" || event.key === "Backspace" ? 200 : 0;
          setTimeout(() => {
            designer.selectStepById(result.focus);
            designer.moveViewportToStep(result.focus);
          }, timeout);
        }
      } else {
        preventDefault = false;
      }
      break;
    }
  }

  if (preventDefault) {
    event.preventDefault();
  }
}

export const shortcuts = {
  findNeedle,
  findNeedleInSwitch,
  findNeedleInSeq,
  navigateUp,
  navigateDown,
  navigateLeft,
  navigateRight,
  navigateEnter,
  navigateToTarget,
  navigateDefinition,
  enterStep,
  toggleStepSkip,
  copyBlock,
  copyBlocks,
  pasteBlock,
  pasteBlocks,
  duplicateBlock,
  duplicateBlocks,
  handleKeydown,
};
